import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";
import ServiceLocator from "../../ServiceLocator";

import Button from "../../lib/Button";
import Card, { card_color_type, card_value_type } from "../game/Card";
import Player from "../game/Player";
import { Signal } from "signals";
import Utils from "../../Utils";


export default class ColorSelectionPopup extends Popup
{
    // #region Attributes //

    // private:

    private _selCard: Card = null;
    private _player: Player = null;

    private _arrColorBtn: Array<Button> = null;
    private _colorSelectHighlight: PIXI.Sprite = null;
    private _frameAnim: PIXI.AnimatedSprite = null;
    private _playerParent: PIXI.Container = null;
    private _title: PIXI.Text = null;

    private _selCardFrom: PIXI.Point = null;

    private _selColor: card_color_type = null;

    private _onColorSelectionStarted: Signal = null;
    private _onColorSelected: Signal = null;

    // #endregion //


    public set player( value: Player ) { this._player = value; }
    public set selCard( value: Card ) { this._selCard = value; }

    public get selColor(): card_color_type { return this._selColor; }
    public get selCard(): Card { return this._selCard; }

    public get onColorSelectionStarted(): Signal { return this._onColorSelectionStarted; }
    public get onColorSelected(): Signal { return this._onColorSelected; }


    // #region Methods //

    // public:

    public onEnter(): void
    {
        super.onEnter();        

        console.assert( this._player != null, "ColorSelectionPopup.ts :: onEnter() :: this._player cannot be null." );
        console.assert( this._selCard != null, "ColorSelectionPopup.ts :: onEnter() :: this._selCard cannot be null." );

        this._onColorSelectionStarted = new Signal();
        this._onColorSelected = new Signal();

        this._arrColorBtn = [];

        this._root.interactiveChildren = false;

        this._selCardFrom = this._root.toLocal( this._selCard.getGlobalPosition() );
        
        this._playerParent = this._player.parent;
        this._player.setArrowVisibility( false );
        for ( let card of this._player.hand )
        {
            card.shade.visible = false;
        }
        this._root.addChild( this._player );

        this._frameAnim = new PIXI.AnimatedSprite( Utils.createTexArray( "frame_anim_", 14 ) );
        this._frameAnim.animationSpeed = 40;
        this._frameAnim.loop = false;
        this._frameAnim.autoUpdate = false;
        this._frameAnim.anchor.set( 0.5 );
        this._frameAnim.scale.set( 1.15 );
        this._frameAnim.visible = false;
        this._frameAnim.onComplete = () => { this._frameAnim.visible = false; }

        this._disabledBg.alpha = 0;
        //
        let tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.timeScale( 1.4 );
        tl.to( this._disabledBg, { alpha: 1, duration: 1, ease: "sine.out" }, 0 );

        const kArrColorTextureId: Array<string> = [
            "color_selection_red",
            "color_selection_yellow",
            "color_selection_blue",
            "color_selection_green" ];
        const kTextureIdSuffix: string = this._selCard.value == card_value_type.WILD_DRAW_FOUR ? "_wild" : "_eight";
        for ( let i: number = 0; i < kArrColorTextureId.length; ++i )
        {
            kArrColorTextureId[ i ] += kTextureIdSuffix;
        }
        const kColorBtnMargin: number = 160;
        let colorBtnX: number = ServiceLocator.app.width * 0.5 - kColorBtnMargin * 1.5;
        for ( let i: number = 0; i < 4; ++i )
        {
            let colorBtn: Button = new Button( { on: kArrColorTextureId[ i ] } );
            colorBtn.value = i;
            colorBtn.anchor.set( 0.5 );
            colorBtn.angle = 45;
            colorBtn.scale.set( 0 );
            colorBtn.position.copyFrom( this._selCardFrom );
            this._root.addChild( colorBtn );
            colorBtn.on( "pointertap", this.onColorBtn_Click, this );
            //
            tl.to( colorBtn, { x: colorBtnX, y: ServiceLocator.app.height * 0.5, angle: 0, duration: 0.4, ease: "sine.out" }, i * 0.2 );
            tl.to( colorBtn.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, i * 0.2 );
            tl.to( this._selCard.scale, { x: 1.1, y: 1.1, duration: 0.05, yoyo: true, repeat: 1 }, i * 0.2 );

            colorBtnX += kColorBtnMargin;

            this._arrColorBtn.push( colorBtn );
        }

        this._title = new PIXI.Text( "Select one color", { fontFamily: "asap_bold", fontSize: 50, fill: 0xffffdf } );
        this._title.anchor.set( 0.5 );
        this._title.scale.set( 0 );
        this._title.position.set( ServiceLocator.app.width * 0.5, 386 );
        this._root.addChild( this._title );
        //
        tl.to( this._title.scale, { x: 1, y: 1, ease: "sine.out", duration: 0.4 }, 0.4 );
        tl.call( () => { 
            this._root.interactiveChildren = true; 

            this._onColorSelectionStarted.dispatch();
        }, null, ">" );

        ServiceLocator.game.tweenGroup.add( tl );

        this._colorSelectHighlight = new PIXI.Sprite( ServiceLocator.resources.color_selection_golden_frame.texture );
        this._colorSelectHighlight.anchor.set( 0.5 );
        this._colorSelectHighlight.scale.set( 1.15 );

        ServiceLocator.audio.playSfx( "wildcard_selection_in" );
    }

        private onColorBtn_Click( event: PIXI.InteractionEvent ): void
        {
            ServiceLocator.audio.playSfx( "wildcard_selection_out" );

            this._root.interactiveChildren = false;

            this._onColorSelected.dispatch();

            let tl: gsap.core.Timeline = new gsap.core.Timeline();
            tl.timeScale( 1.4 );

            this._selColor = ( event.currentTarget as Button ).value;
            for ( let i: number = 0; i <= this._selColor - 1; ++i )
            {
                this._root.addChild( this._arrColorBtn[ i ] );

                tl.to( this._arrColorBtn[ i ], { angle: -15, duration: ( 0.6 - i * 0.2 ) * 0.4, ease: "sine.out" }, i * 0.2 );
                tl.to( this._arrColorBtn[ i ], { x: this._arrColorBtn[ this._selColor ].x, duration: 0.6 - i * 0.2, ease: "back.in" }, i * 0.2 );
            }
            for ( let i: number = this._selColor + 1; i <= this._arrColorBtn.length - 1; ++i )
            {
                this._root.addChild( this._arrColorBtn[ i ] );
            }
            let buttonCount: number = 0;
            for ( let i: number = this._arrColorBtn.length - 1; i >= this._selColor + 1; -- i )
            {
                tl.to( this._arrColorBtn[ i ], { angle: 15, duration: ( 0.6 - buttonCount * 0.2 ) * 0.4, ease: "sine.out" }, i * 0.2 );
                tl.to( this._arrColorBtn[ i ], { x: this._arrColorBtn[ this._selColor ].x, duration: 0.6 - buttonCount * 0.2, ease: "back.in" }, buttonCount * 0.2 );
            
                buttonCount += 1;
            }
            //this._root.addChild( this._arrColorBtn[ this._selColor ] );

            //this._root.addChild( this._colorSelectHighlight );
            //this._colorSelectHighlight.position.copyFrom( this._arrColorBtn[ this._selColor ].position );
            let selColorWrapper: PIXI.Container = new PIXI.Container();
            selColorWrapper.position.copyFrom( this._arrColorBtn[ this._selColor ].position );
            this._arrColorBtn[ this._selColor ].position.set( 0 );
            selColorWrapper.addChild( this._arrColorBtn[ this._selColor ] );
            selColorWrapper.addChild( this._colorSelectHighlight );
            this._root.addChild( selColorWrapper );
            //
            this._root.addChild( this._frameAnim );
            this._frameAnim.position.copyFrom( selColorWrapper.position );
            this._frameAnim.visible = true;
            this._frameAnim.play();

            tl.to( selColorWrapper.scale, { x: 1.2, y: 1.2, duration: 0.2, yoyo: true, repeat: 1, ease: "sine.out" }, 0 );
            tl.to( this._colorSelectHighlight, { alpha: 0, duration: 0.2, ease: "sine.out" }, 0.4 );

            tl.call( () => {
                for ( let j: number = 0; j <= this._arrColorBtn.length - 1; ++j )
                {
                    if ( j != this._selColor )
                    {
                        this._arrColorBtn[ j ].visible = false;
                    }
                }
            }, null, 0.6 );
            //
            tl.to( this._disabledBg, { alpha: 0, duration: 0.4, ease: "sine.in" }, 0.6 );
            tl.to( this._title.scale, { x: 0, y: 0, duration: 0.4, ease: "sine.in" }, 0.6 );
            //
            tl.to( selColorWrapper.scale, { x: 1.4, y: 1.4, duration: 0.2, yoyo: true, repeat: 1, ease: "sine.out" }, 0.6 );
            tl.to( selColorWrapper, { angle: this._selColor <= 1 ? -30 : 30, duration: 0.6, ease: "sine.out" }, 0.6 );
            tl.to( selColorWrapper, { x: this._selCardFrom.x, y: this._selCardFrom.y, duration: 0.4, ease: "sine.out" }, 1 );
            tl.to( selColorWrapper.scale, { x: 0, y: 0, duration: 0.4, ease: "sine.out" }, 1 );
            //
            tl.to( this._selCard.scale, { x: 1.2, y: 1.2, duration: 0.2, yoyo: true, repeat: 1, ease: "sine.out" }, 1.4 );
            //
            tl.call( () => { 
                this._playerParent.addChild( this._player );
                this._player.setArrowVisibility( true );

                this._onClosed.dispatch( this ); }, null );

            ServiceLocator.game.tweenGroup.add( tl );
        }

    public onLeave(): void
    {
        this._onColorSelectionStarted.dispose();
        this._onColorSelectionStarted = null;

        this._onColorSelected.dispose();
        this._onColorSelected = null;

        super.onLeave();
    }

    public onUpdate( dt: number ): void
    {
        if ( this._frameAnim.visible )
        {
            this._frameAnim.update( dt );
        }
    }
    
    public isBusy(): boolean 
    { 
        return !this._root.interactiveChildren; 
    }

    public onAutoPlayed(): void
    {
        this._playerParent.addChild( this._player );
    }

    // #endregion //
}